import React, { useState, useEffect } from "react";
import { NavButton, NavLink } from "./nav-link";
import { Disclosure, Popover, Transition } from "@headlessui/react";
import { H1, H4, PS } from "@components/ui/typography";
import Headroom from "headroom.js";
import { link } from "./navbar.module.css";
import { Link } from "gatsby";
import { container } from "@styles/common-styles.module.css";
import Button from "../ui/button";
// import Logo from "../../images/rf-web-01.svg";
import Logo from "../../images/rf-logo.svg";
import MobileLogo from "../../images/rf-logo-mobile.svg";
import ChevronDown from "../../images/chevron-down-big.svg";

interface NavbarProps {}

const Navbar: React.FC<NavbarProps> = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const handleMenuClick = () => {
    document.body.style.overflow = isMenuOpen ? "auto" : "hidden";
    setIsMenuOpen(!isMenuOpen);
  };

  useEffect(() => {
    const headroom = new Headroom(document.querySelector("header") as HTMLElement, {
      tolerance: 5,
      classes: {
        pinned: "pinned",
        unpinned: "unpinned",
        top: "top",
        notTop: "not-top",
        bottom: "bottom",
        notBottom: "not-bottom",
        frozen: "frozen"
      }
    });
    headroom.init();

    return () => {
      headroom.destroy();
    };
  }, []);

  return (
    <header className={`py-3 w-full relative z-20 initial`}>
      <div className={`w-full h-full ${container} px-6 min-[1440px]:px-0`}>
        <div className="flex justify-between items-center tab:hidden h-full">
          <Link to="/" className="inline-flex items-center relative z-20">
            <img src={MobileLogo} alt="Railsfactory logo" width={44} height={40} />
          </Link>
          <div className="flex items-center">
            <button
              className={`p-2 -mr-2 relative z-20 hamburger hamburger--squeeze flex items-center ${
                isMenuOpen ? "is-active" : ""
              }`}
              onClick={handleMenuClick}
              type="button"
            >
              <span className="hamburger-box">
                <span className="hamburger-inner"></span>
              </span>
            </button>
            <div
              className={`transition transform bg-emerald-50 duration-[1s] fixed -top-[500px] -right-[500px] w-10 h-10 rounded-full will-change-transform ${
                isMenuOpen ? "scale-[100]" : "scale-0"
              }`}
            ></div>
            <Transition show={isMenuOpen}>
              <div className="absolute inset-0 z-10">
                <div className="h-[100vh] pt-[calc(theme(spacing.navbar-height)+2rem)] w-full px-6">
                  <Transition.Child
                    enter="transition-[translate_opacity] duration-200 ease-out delay-700"
                    enterFrom="transform translate-x-[-100%] opacity-0"
                    enterTo="transform translate-x-0 opacity-100"
                    leave="transition-[translate_opacity] duration-300 ease-out delay-0"
                    leaveFrom="transform translate-x-0 opacity-100"
                    leaveTo="transform translate-x-[-100%] opacity-0"
                  >
                    <div className="h-[calc(100vh-160px-theme(spacing.navbar-height))] overflow-scroll">
                      <Link to="/" onClick={handleMenuClick}>
                        <H1 className="py-3">_Home</H1>
                      </Link>
                      <Disclosure>
                        {({ open }) => (
                          <>
                            <Disclosure.Button className="block w-full text-left">
                              <H1 className="py-3">
                                _Services
                                <img
                                  src={ChevronDown}
                                  alt="Dropdown icon"
                                  className={`inline-block ml-3 transition-transform duration-200 ${
                                    open ? "rotate-180" : ""
                                  }`}
                                />
                              </H1>
                            </Disclosure.Button>
                            <Transition
                              enter="transition-[max-height_opacity] duration-200 ease-out"
                              enterFrom="transform max-h-0 opacity-0"
                              enterTo="transform max-h-[260px] opacity-100"
                              leave="transition-[max-height_opacity] duration-100 ease-out"
                              leaveFrom="transform max-h-[260px] opacity-100"
                              leaveTo="transform max-h-0 opacity-0"
                            >
                              <Disclosure.Panel>
                                <Link onClick={handleMenuClick} to="/services">
                                  <H4 className="py-3">Overview</H4>
                                </Link>
                                <Link onClick={handleMenuClick} to="/hire-ruby-on-rails-developer">
                                  <H4 className="py-3">Hire Ruby on Rails Developer</H4>
                                </Link>
                                <Link
                                  onClick={handleMenuClick}
                                  to="/custom-application-development-service"
                                >
                                  <H4 className="py-3">Custom software development</H4>
                                </Link>
                                <Link onClick={handleMenuClick} to="/rails-upgrade-services">
                                  <H4 className="py-3">Rails upgrades & maintenance</H4>
                                </Link>
                                <Link onClick={handleMenuClick} to="/app-modernization-service">
                                  <H4 className="py-3">App modernization services</H4>
                                </Link>
                                <Link onClick={handleMenuClick} to="/dedicated-development-team">
                                  <H4 className="py-3">Dedicated team</H4>
                                </Link>
                              </Disclosure.Panel>
                            </Transition>
                          </>
                        )}
                      </Disclosure>
                      <Link to="/rails-calculator" onClick={handleMenuClick}>
                        <H1 className="py-3">_Rails_estimator</H1>
                      </Link>
                      <Link to="/case-studies" onClick={handleMenuClick}>
                        <H1 className="py-3">_Case_studies</H1>
                      </Link>
                      <Link to="/about-us" onClick={handleMenuClick}>
                        <H1 className="py-3">_About us</H1>
                      </Link>
                      <Link to="/blog" onClick={handleMenuClick}>
                        <H1 className="py-3">_Blog</H1>
                      </Link>
                    </div>
                  </Transition.Child>
                  <Transition.Child
                    enter="transition-[translate_opacity] duration-200 ease-out delay-700"
                    enterFrom="transform translate-y-[100%] opacity-0"
                    enterTo="transform translate-y-0 opacity-100"
                    leave="transition-[translate_opacity] duration-300 ease-out delay-0"
                    leaveFrom="transform translate-y-0 opacity-100"
                    leaveTo="transform translate-y-[100%] opacity-0"
                  >
                    <div className="w-full flex">
                      <Link to="/contact" onClick={handleMenuClick} className="block w-full">
                        <Button size="lg" className="block w-full">
                          Contact us
                        </Button>
                      </Link>
                    </div>
                  </Transition.Child>
                </div>
              </div>
            </Transition>
          </div>
        </div>
        <div className="hidden tab:flex md:justify-between md:gap-4 h-full">
          <Link to="/" className="flex grow-0 shrink-0 items-center">
            <img src={Logo} alt="Logo" />
          </Link>
          <nav className="flex  justify-end items-center">
            <NavLink to="/">Home</NavLink>
            <Popover className="relative">
              {({ open }) => (
                <>
                  <Popover.Button className={`${link} cursor-pointer`}>
                    Services
                    <img
                      src={ChevronDown}
                      alt="Dropdown icon"
                      className={`inline-block ml-2 transition-transform duration-200 ${
                        open ? "rotate-180" : ""
                      }`}
                      width={16}
                      height={16}
                    />
                  </Popover.Button>
                  <Popover.Panel className="w-64 bg-emerald-50 border border-dark absolute top-12 z-10 -left-32 max-w-max py-2">
                    <Link to="/services" className="block px-4 py-2 hover:bg-emerald-100">
                      <PS>Overview</PS>
                    </Link>
                    <Link
                      to="/hire-ruby-on-rails-developer"
                      className="block px-4 py-2 hover:bg-emerald-100"
                    >
                      <PS>Hire Ruby on Rails Developer</PS>
                    </Link>
                    <Link
                      to="/custom-application-development-service"
                      className="block px-4 py-2 hover:bg-emerald-100"
                    >
                      <PS>Custom software development</PS>
                    </Link>
                    <Link
                      to="/rails-upgrade-services"
                      className="block px-4 py-2 hover:bg-emerald-100"
                    >
                      <PS>Rails upgrades & maintenance</PS>
                    </Link>
                    <Link
                      to="/app-modernization-service"
                      className="block px-4 py-2 hover:bg-emerald-100"
                    >
                      <PS>App modernization service</PS>
                    </Link>
                    <Link
                      to="/dedicated-development-team"
                      className="block px-4 py-2 hover:bg-emerald-100"
                    >
                      <PS>Dedicated team</PS>
                    </Link>
                  </Popover.Panel>
                </>
              )}
            </Popover>
            <NavLink to="/rails-calculator">Rails_estimator</NavLink>
            <NavLink to="/case-studies">Case_studies</NavLink>
            <NavLink to="/about-us">About_us</NavLink>
            <NavLink to="/blog">Blog</NavLink>
            <NavButton to="/contact">Contact us</NavButton>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
